import React, { useState, useEffect } from 'react';

const CalendarView = ({ homeworkAssignments, selectedDate, onDateSelect }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(false);

  const monthNames = [
    '一月', '二月', '三月', '四月', '五月', '六月',
    '七月', '八月', '九月', '十月', '十一月', '十二月'
  ];

  const dayNames = ['日', '一', '二', '三', '四', '五', '六'];

  const loadCalendarData = React.useCallback(async () => {
    try {
      setLoading(true);
      
      // Process homework assignments for calendar display
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      
      const data = {};
      
      // Group homework by date
      homeworkAssignments.forEach(homework => {
        const date = new Date(homework.assigned_date);
        const dateStr = date.toISOString().split('T')[0];
        
        if (date.getFullYear() === year && date.getMonth() === month) {
          if (!data[dateStr]) {
            data[dateStr] = {
              homework: [],
              count: 0
            };
          }
          data[dateStr].homework.push(homework);
          data[dateStr].count++;
        }
      });
      
      setCalendarData(data);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDate, homeworkAssignments]);

  useEffect(() => {
    loadCalendarData();
  }, [currentDate, homeworkAssignments, loadCalendarData]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const handleDateClick = (date) => {
    if (date) {
      const dateStr = date.toISOString().split('T')[0];
      onDateSelect(dateStr);
    }
  };

  const getDateStatus = (date) => {
    if (!date) return null;
    
    const dateStr = date.toISOString().split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    const selected = selectedDate;
    
    if (dateStr === today) return 'today';
    if (dateStr === selected) return 'selected';
    
    const dayData = calendarData[dateStr];
    if (dayData && dayData.count > 0) {
      return 'has-homework';
    }
    
    return 'normal';
  };

  const getDateData = (date) => {
    if (!date) return null;
    const dateStr = date.toISOString().split('T')[0];
    return calendarData[dateStr] || null;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-100 text-red-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const getAssignmentTypeIcon = (type) => {
    const icons = {
      '練習': '📝',
      '作文': '✍️',
      '實驗報告': '🔬',
      '研究報告': '📖',
      '背誦': '🎭',
      '繪圖': '🎨'
    };
    return icons[type] || '📋';
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="calendar-view">
      <div className="calendar-header">
        <h3>家課日曆</h3>
        <p>顯示整月的家課分布</p>
      </div>

      <div className="calendar-navigation">
        <button 
          className="nav-btn"
          onClick={() => navigateMonth(-1)}
        >
          ←
        </button>
        <h4>{currentDate.getFullYear()}年 {monthNames[currentDate.getMonth()]}</h4>
        <button 
          className="nav-btn"
          onClick={() => navigateMonth(1)}
        >
          →
        </button>
      </div>

      <div className="calendar-grid">
        <div className="calendar-header-row">
          {dayNames.map(day => (
            <div key={day} className="calendar-day-header">
              {day}
            </div>
          ))}
        </div>
        
        <div className="calendar-body">
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="calendar-day empty"></div>;
            }

            const status = getDateStatus(date);
            const dayData = getDateData(date);
            const isSelected = selectedDate && date.toISOString().split('T')[0] === selectedDate;

            return (
              <div 
                key={index} 
                className={`calendar-day ${status} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleDateClick(date)}
              >
                <div className="day-number">{date.getDate()}</div>
                
                {dayData && dayData.count > 0 && (
                  <div className="day-homework">
                    {dayData.homework.slice(0, 2).map((homework, hwIndex) => (
                      <div 
                        key={hwIndex} 
                        className={`homework-preview ${getPriorityColor(homework.priority_level)}`}
                        title={`${homework.subject} - ${homework.assignment_title}`}
                      >
                        <span className="homework-icon">
                          {getAssignmentTypeIcon(homework.assignment_type)}
                        </span>
                        <span className="homework-subject">
                          {homework.subject}
                        </span>
                      </div>
                    ))}
                    {dayData.count > 2 && (
                      <div className="more-homework">
                        +{dayData.count - 2} 更多
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="calendar-legend">
        <h4>圖例說明</h4>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color today"></div>
            <span>今天</span>
          </div>
          <div className="legend-item">
            <div className="legend-color selected"></div>
            <span>選中日期</span>
          </div>
          <div className="legend-item">
            <div className="legend-color has-homework"></div>
            <span>有家課的日期</span>
          </div>
          <div className="legend-item">
            <span className="legend-icon">🔴</span>
            <span>高優先級</span>
          </div>
          <div className="legend-item">
            <span className="legend-icon">🟡</span>
            <span>中優先級</span>
          </div>
          <div className="legend-item">
            <span className="legend-icon">🟢</span>
            <span>低優先級</span>
          </div>
        </div>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>載入日曆資料中...</p>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
